import Markdown from 'react-markdown';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import { Box, useMediaQuery } from '@mui/material';
import React, { useMemo } from 'react';

// Types for component props
interface ImageProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node?: any;
  alt?: string;
  src?: string;
  title?: string;
}

interface LinkProps {
  href?: string;
  children?: React.ReactNode;
}

interface MarkdownDisplayProps {
  markdown: string;
  styles: {
    markdownContainer: string;
    aboutLinks: string;
    defaultImage: string;
    smallImage: string;
    mainImage: string;
    markdownTable: string;
    markdownTableCell: string;
    markdownInnerContainer: string;
  };
  contentConfig: {
    useExternalDirectory: boolean;
    externalBaseUrl: string;
    imagesSubdir: string;
  };
  onMarkdownChange?: (newMarkdown: string, filename?: string) => void;
}

const MarkdownDisplay: React.FC<MarkdownDisplayProps> = ({
  markdown,
  styles,
  contentConfig,
  onMarkdownChange,
}) => {
  // Use MUI useMediaQuery for mobile detection
  const isMobile = useMediaQuery('(max-width:768px)');

  // Function to handle markdown file clicks
  const handleMarkdownLink = async (href: string) => {
    if (!onMarkdownChange || !href.endsWith('.md')) {
      return false; // Not a markdown file or no handler provided
    }

    try {
      // Construct the full URL for the markdown file
      const markdownUrl = contentConfig.useExternalDirectory
        ? `${contentConfig.externalBaseUrl}/${href}`
        : `/${href}`;

      const response = await fetch(markdownUrl);
      if (!response.ok) {
        console.error(`Failed to fetch markdown file: ${markdownUrl}`);
        return false;
      }

      const newMarkdown = await response.text();

      // Check if it's actually markdown content (not HTML)
      if (
        newMarkdown.startsWith('<!DOCTYPE html>') ||
        newMarkdown.includes('<html')
      ) {
        console.warn('Received HTML instead of markdown — ignoring');
        return false;
      }

      const container = document.querySelector(`.${styles.markdownContainer}`);
      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      // Call the callback to update the markdown
      onMarkdownChange(newMarkdown, href);

      // Scroll to top of the markdown container after content loads
      /*setTimeout(() => {
        const container = document.querySelector(
          `.${styles.markdownContainer}`
        );
        if (container) {
          container.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }, 100); // Small delay to ensure content has rendered*/

      return true;
    } catch (error) {
      console.error('Error fetching markdown file:', error);
      return false;
    }
  };

  // Memoize plugins to prevent recreation on every render
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const rehypePlugins = useMemo(
    () =>
      [
        rehypeRaw,
        rehypeSlug,
        [rehypeAutolinkHeadings, { behavior: 'append' }],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ] as any,
    []
  );

  return (
    <Box className={styles.markdownContainer}>
      <Box className={styles.markdownInnerContainer}>
        <Markdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={{
            a: ({ href, children }: LinkProps) => {
              const isExternal = href?.startsWith('http');
              const isMarkdownFile = href?.endsWith('.md');

              if (href?.startsWith('#')) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      const id = href.replace('#', '');
                      const container = document.querySelector(
                        `.${styles.markdownContainer}`
                      );
                      const el = document.getElementById(id);
                      if (el && container) {
                        container.scrollTo({
                          top: el.offsetTop - 80,
                          behavior: 'smooth',
                        });
                      }
                    }}
                    className="about-link"
                  >
                    {children}
                  </a>
                );
              }

              if (isMarkdownFile && onMarkdownChange && href) {
                return (
                  <a
                    href={href}
                    className="about-link"
                    onClick={async (e) => {
                      e.preventDefault();
                      const success = await handleMarkdownLink(href);
                      if (!success) {
                        // Fallback to default link behavior if markdown fetch fails
                        window.location.href = href;
                      }
                    }}
                    aria-label={`Load ${href} content`}
                  >
                    {children}
                  </a>
                );
              }

              return (
                <a
                  href={href}
                  className="about-link"
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  aria-label={
                    isExternal ? `${children} (opens in new tab)` : undefined
                  }
                >
                  {children}
                </a>
              );
            },
            p: ({ children, ...props }) => {
              // Check if this paragraph contains multiple hash anchor links
              const childArray = React.Children.toArray(children);

              // Count React link elements that have href starting with #
              const hashLinkCount = childArray.filter((child) => {
                if (React.isValidElement(child)) {
                  const childProps = child.props as { href?: string };
                  return childProps.href?.startsWith('#');
                }
                return false;
              }).length;

              // Check if there are pipe separators between links
              const hasMultiplePipes =
                childArray.filter(
                  (child) => typeof child === 'string' && child.includes('|')
                ).length > 0;

              const isNavLinks = hashLinkCount >= 3 && hasMultiplePipes;

              if (isNavLinks) {
                // Filter out text nodes (including pipes) and keep only link elements for both mobile and desktop
                const linkElements = childArray.filter((child) => {
                  if (React.isValidElement(child)) {
                    const childProps = child.props as { href?: string };
                    return childProps.href?.startsWith('#');
                  }
                  return false;
                });

                if (isMobile) {
                  return (
                    <div className={styles.aboutLinks} {...props}>
                      {linkElements}
                    </div>
                  );
                } else {
                  // Desktop: use div with filtered links (no pipes)
                  return (
                    <div className={styles.aboutLinks} {...props}>
                      {linkElements}
                    </div>
                  );
                }
              }
              return <p {...props}>{children}</p>;
            },
            img: ({ node, ...props }: ImageProps) => {
              const title = (
                node && typeof node === 'object' && 'properties' in node
                  ? (node.properties as { title?: string }).title
                  : undefined
              ) as string | undefined;

              let className = styles.defaultImage;
              if (title === 'small-img') {
                className = styles.smallImage;
              } else if (title === 'main-img') {
                className = styles.mainImage;
              }

              // Transform image src to use external directory if configured
              const transformImageSrc = (originalSrc?: string) => {
                if (!originalSrc) return originalSrc;

                if (contentConfig.useExternalDirectory) {
                  // If the src starts with 'images/', use the external path directly
                  if (originalSrc.startsWith('images/')) {
                    return `${contentConfig.externalBaseUrl}/${originalSrc}`;
                  }
                  // If it's a relative path without directory prefix and not an absolute URL, assume it's in the images directory
                  if (
                    !originalSrc.startsWith('http') &&
                    !originalSrc.startsWith('/') &&
                    !originalSrc.startsWith('data:') &&
                    !originalSrc.includes('/')
                  ) {
                    return `${contentConfig.externalBaseUrl}/${contentConfig.imagesSubdir}/${originalSrc}`;
                  }
                } else {
                  // When using local files, transform 'images/' to 'about/' for backward compatibility
                  if (originalSrc.startsWith('images/')) {
                    return originalSrc.replace('images/', 'about/');
                  }
                }

                return originalSrc;
              };

              const alt = props.alt ?? '';
              const transformedSrc = transformImageSrc(props.src);

              return (
                <img
                  {...props}
                  src={transformedSrc}
                  className={className}
                  alt={alt}
                />
              );
            },
            table: ({ children }) => (
              <table className={styles.markdownTable}>{children}</table>
            ),
            td: ({ children, ...props }) => (
              <td className={styles.markdownTableCell} {...props}>
                {children}
              </td>
            ),
            div: ({ node, ...props }) => {
              return <div {...props}>{props.children}</div>;
            },
          }}
        >
          {markdown}
        </Markdown>
      </Box>
    </Box>
  );
};

export default MarkdownDisplay;
